/**
 * Pure rules for the site layout views (Cleaning Visual, z8nrz7f731): counts,
 * the next building code, and where each building sits on the campus
 * map. Kept out of the components so they can be tested.
 */
import type { LayoutBuilding, LayoutFloor, SiteLayout } from '@/api/facilityApi'

/** "1 room", "3 rooms". */
export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

export function roomCount(floors: readonly LayoutFloor[]): number {
  return floors.reduce((n, f) => n + f.rooms.length, 0)
}

/** Every room the layout knows of, placed or not. */
export function totalRooms(layout: SiteLayout): number {
  return (
    layout.buildings.reduce((n, b) => n + roomCount(b.floors), 0) +
    layout.unmappedRooms.length
  )
}

/** Floors bottom-up, as a building's floor tabs read. */
export function sortFloors<T extends { level: number; name: string }>(floors: readonly T[]): T[] {
  return [...floors].sort((a, b) => a.level - b.level || a.name.localeCompare(b.name, undefined, { numeric: true }))
}

/** The next unused single-letter code: A, B, … Z, then AA, AB, … */
export function nextBuildingCode(taken: readonly string[]): string {
  const used = new Set(taken.map((c) => c.trim().toUpperCase()))
  const letter = (i: number) => String.fromCharCode(65 + i)
  for (let i = 0; i < 26; i++) if (!used.has(letter(i))) return letter(i)
  for (let i = 0; i < 26; i++) for (let j = 0; j < 26; j++) {
    const code = letter(i) + letter(j)
    if (!used.has(code)) return code
  }
  return String(used.size + 1)
}

export interface MapBox {
  id: string
  /** Position and size in campus-grid cells. */
  x: number
  y: number
  w: number
  h: number
}

/** The drawn site plan's grid (matches the API's MAP_SIZE): buildings are placed and sized in its cells. */
export const MAP_GRID = { cols: 40, rows: 24 } as const

/** A building not placed by hand yet is drawn this big. */
export const DEFAULT_FOOTPRINT = { w: 12, h: 7 }

type Placeable = Pick<LayoutBuilding, 'id' | 'mapX' | 'mapY' | 'mapW' | 'mapH'>

/**
 * Where each building sits on the drawn site plan. A building placed by hand
 * (dragged and sized on the Layout) keeps its place; the rest fill two
 * staggered columns, as the spec's campus view shows (A top left, B to its
 * right and lower, C below A). The grid grows when the automatic placement
 * runs past it.
 */
export function campusBoxes(
  buildings: readonly Placeable[],
): { boxes: MapBox[]; cols: number; rows: number } {
  const GAP = 2
  const { w, h } = DEFAULT_FOOTPRINT
  const colX = [2, 2 + w + 3]
  const cursor = [2, 2 + Math.ceil((h + GAP) / 2)]
  let auto = 0
  const placed: MapBox[] = buildings.flatMap((b) =>
    b.mapX !== null && b.mapY !== null && b.mapW !== null && b.mapH !== null ? [{ id: b.id, x: b.mapX, y: b.mapY, w: b.mapW, h: b.mapH }] : [],
  )
  // A cell's clearance around every box taken so far, so a building not placed
  // by hand (say, one a site plan picture did not show) never lands on one that was.
  const free = (box: MapBox) => taken.every((t) => box.x + box.w + 1 <= t.x || t.x + t.w + 1 <= box.x || box.y + box.h + 1 <= t.y || t.y + t.h + 1 <= box.y)
  const taken: MapBox[] = [...placed]
  const boxes = buildings.map((b): MapBox => {
    const own = placed.find((p) => p.id === b.id)
    if (own) return own
    const col = auto++ % 2
    let box = { id: b.id, x: colX[col], y: cursor[col], w, h }
    cursor[col] += h + GAP
    if (!free(box)) {
      // The usual slot is taken: the first free one on the grid, else below everything.
      const spot = Array.from({ length: Math.max(0, MAP_GRID.rows - h + 1) * (MAP_GRID.cols - w + 1) }, (_, i) => ({
        id: b.id,
        x: i % (MAP_GRID.cols - w + 1),
        y: Math.floor(i / (MAP_GRID.cols - w + 1)),
        w,
        h,
      })).find(free)
      box = spot ?? { id: b.id, x: 2, y: Math.max(...taken.map((t) => t.y + t.h)) + GAP, w, h }
    }
    taken.push(box)
    return box
  })
  const cols = Math.max(MAP_GRID.cols, ...boxes.map((b) => b.x + b.w + 1))
  const rows = Math.max(MAP_GRID.rows, ...boxes.map((b) => b.y + b.h + 1))
  return { boxes, cols, rows }
}

/** A box moved or resized by hand, kept whole inside the grid and at least 2 × 2 cells. */
export function clampBox(box: MapBox, rows: number = MAP_GRID.rows): MapBox {
  const w = Math.min(MAP_GRID.cols, Math.max(2, Math.round(box.w)))
  const h = Math.min(rows, Math.max(2, Math.round(box.h)))
  return {
    id: box.id,
    w,
    h,
    x: Math.min(MAP_GRID.cols - w, Math.max(0, Math.round(box.x))),
    y: Math.min(rows - h, Math.max(0, Math.round(box.y))),
  }
}

/** Where the line between two boxes' centres leaves `a`'s edge. */
function edgePoint(a: MapBox, toward: { x: number; y: number }) {
  const cx = a.x + a.w / 2
  const cy = a.y + a.h / 2
  const dx = toward.x - cx
  const dy = toward.y - cy
  if (dx === 0 && dy === 0) return { x: cx, y: cy }
  const t = Math.min(dx === 0 ? Infinity : a.w / 2 / Math.abs(dx), dy === 0 ? Infinity : a.h / 2 / Math.abs(dy))
  return { x: cx + dx * t, y: cy + dy * t }
}

/**
 * The connector between two buildings, edge to edge. Boxes side by side (or
 * one above the other) get a straight horizontal (vertical) link through the
 * middle of what they share, like a corridor; otherwise the line runs between
 * their centres, cut at each edge.
 */
export function linkSegment(a: MapBox, b: MapBox): { x1: number; y1: number; x2: number; y2: number } {
  const top = Math.max(a.y, b.y)
  const bottom = Math.min(a.y + a.h, b.y + b.h)
  if (bottom - top > 0) {
    const y = (top + bottom) / 2
    const [l, r] = a.x + a.w <= b.x ? [a, b] : [b, a]
    return { x1: l.x + l.w, y1: y, x2: r.x, y2: y }
  }
  const left = Math.max(a.x, b.x)
  const right = Math.min(a.x + a.w, b.x + b.w)
  if (right - left > 0) {
    const x = (left + right) / 2
    const [t, u] = a.y + a.h <= b.y ? [a, b] : [b, a]
    return { x1: x, y1: t.y + t.h, x2: x, y2: u.y }
  }
  const p = edgePoint(a, { x: b.x + b.w / 2, y: b.y + b.h / 2 })
  const q = edgePoint(b, { x: a.x + a.w / 2, y: a.y + a.h / 2 })
  return { x1: p.x, y1: p.y, x2: q.x, y2: q.y }
}

/**
 * Buildings drawn larger, each around its own centre, so a small panel spends
 * less of itself on the space between them: as large as they can grow before
 * any two come within `gap` cells of each other, and at most `maxScale`. The
 * arrangement (where each sits, what is next to what) does not change.
 */
export function inflateBoxes(boxes: readonly MapBox[], { maxScale = 1.6, gap = 1 } = {}): MapBox[] {
  let scale = maxScale
  for (let i = 0; i < boxes.length; i++) {
    for (let j = i + 1; j < boxes.length; j++) {
      const a = boxes[i]
      const b = boxes[j]
      const dx = Math.abs(a.x + a.w / 2 - (b.x + b.w / 2))
      const dy = Math.abs(a.y + a.h / 2 - (b.y + b.h / 2))
      // Apart along either axis is enough: the larger of the two limits holds.
      const fit = Math.max((dx - gap) / ((a.w + b.w) / 2), (dy - gap) / ((a.h + b.h) / 2))
      scale = Math.min(scale, fit)
    }
  }
  scale = Math.max(1, scale)
  return boxes.map((b) => ({
    id: b.id,
    x: b.x + (b.w - b.w * scale) / 2,
    y: b.y + (b.h - b.h * scale) / 2,
    w: b.w * scale,
    h: b.h * scale,
  }))
}
